import { authService } from './authService';
import { NewRequestFormData } from '../pages/employees/leaves/NewRequestDialog';

const LEAVE_API_URL = '/api/leave-requests/';
const MANAGED_LEAVE_API_URL = '/api/managed-department-leave-requests/';

export const leaveService = {
  getLeaveRequests: async (filters?: any) => {
    const token = authService.getAccessToken();
    
    let managedUrl = MANAGED_LEAVE_API_URL;
    if (filters) {
      const queryParams = new URLSearchParams(filters).toString();
      managedUrl = `${managedUrl}?${queryParams}`;
    }

    let response = await fetch(managedUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (response.status === 403 || response.status === 404) {
      let globalUrl = LEAVE_API_URL;
      if (filters) {
        const queryParams = new URLSearchParams(filters).toString();
        globalUrl = `${globalUrl}?${queryParams}`;
      }

      response = await fetch(globalUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
    }

    if (!response.ok) {
      return [];
    }
    
    return response.json();
  },
  createLeaveRequest: async (data: NewRequestFormData) => {
    const token = authService.getAccessToken();
    const response = await fetch(LEAVE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify(data),
    });

    const responseData = await response.json().catch(() => ({}));

    if (!response.ok) {
      let errorMessage = 'Erreur lors de la création de la demande.';
      
      if (responseData.__all__ && Array.isArray(responseData.__all__)) {
        errorMessage = responseData.__all__[0];
      } else if (responseData.detail) {
        errorMessage = typeof responseData.detail === 'string' ? responseData.detail : JSON.stringify(responseData.detail);
      } else {
        const firstKey = Object.keys(responseData)[0];
        if (firstKey) {
          const errVal = responseData[firstKey];
          errorMessage = Array.isArray(errVal) ? errVal[0] : String(errVal);
        }
      }

      throw new Error(errorMessage);
    }
    
    return responseData;
  },
  updateLeaveRequest: async (id: number | string, data: NewRequestFormData) => {
    const token = authService.getAccessToken();
    const response = await fetch(`${LEAVE_API_URL}${id}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || errData.message || 'Erreur lors de la mise à jour de la demande.');
    }
    return response.json();
  },

  updateLeaveStatus: async (id: number, status: 'approved' | 'rejected') => {
    const token = authService.getAccessToken();
    const response = await fetch(`${LEAVE_API_URL}${id}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || 'Erreur lors de la mise à jour');
    }
    return response.json();
  },

  submitLeaveRequest: async (id: number) => {
    const token = authService.getAccessToken();
    const response = await fetch(`${LEAVE_API_URL}${id}/submit/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || 'Erreur lors de la soumission');
    }
    return response.json();
  },

  approveLeaveRequest: async (id: number) => {
    const token = authService.getAccessToken();
    const response = await fetch(`${LEAVE_API_URL}${id}/approve/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || "Erreur lors de l'approbation");
    }
    return response.json();
  },

  rejectLeaveRequest: async (id: number, managerNote?: string) => {
    const token = authService.getAccessToken();
    const response = await fetch(`${LEAVE_API_URL}${id}/reject/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({ manager_note: managerNote || '' }),
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || 'Erreur lors du refus');
    }
    return response.json();
  },

  cancelLeaveRequest: async (id: number) => {
    const token = authService.getAccessToken();
    const response = await fetch(`${LEAVE_API_URL}${id}/cancel/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || "Impossible d'annuler la demande");
    }
    return response.json();
  },

  deleteLeaveRequest: async (id: number | string): Promise<void> => {
    const token = authService.getAccessToken();
    const response = await fetch(`${LEAVE_API_URL}${id}/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Erreur lors de la suppression de la demande.');
    }
  },
};